import { Divider, Group, IconFont, Input, SegmentedControl, Select, Tooltip } from '@byecode/ui'
import type { IndependentAction, OpenPageActionConfig, PageStackDisplayType, RichTextContentProtocol } from '@lighthouse/core'
import { PAGE_STACK_DISPLAY_TYPE } from '@lighthouse/core'
import type { FlowNode, NodeTypes } from '@lighthouse/shared'
import { commonPageList, ListItem4ByecodeUi, NO_NAME_PAGE, nodeTypeOptions, SelectPage, useAtomData } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import { useAtomCallback } from 'jotai/utils'
import React, { useCallback, useMemo } from 'react'

import { languageAtom } from '@/atoms/application/state'
import { createPageAtom, openPageStackAtom } from '@/atoms/page/action'
import { pageTreeListAtom } from '@/atoms/page/state'
import { stackFactory } from '@/atoms/page/utils'
import { ArgInput } from '@/components/ArgInput'
import { PAGE_STACK_DISPLAY_TYPE_OPTIONS } from '@/constants/flow'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useAppLanguageList } from '@/hooks/useApplication'
import { useCurrentLanguagePageList } from '@/hooks/usePage'

import * as SC from '../styles'

interface OpenPageActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    actionTypeSwitcher?: React.ReactNode
    value: OpenPageActionConfig
    onChange: (value: OpenPageActionConfig) => void
}

export const OpenPageActionConfigure: React.FC<OpenPageActionConfigureProps> = ({
    actionTypeSwitcher,
    allParentNodes,
    allParentActions,
    nodeType,
    value,
    onChange
}) => {
    const { openPageId, pageOpenType, pathParameters, queryParameters, hash } = value
    const allPages = useCurrentLanguagePageList()
    const pageTree = useAtomData(pageTreeListAtom)
    const languageList = useAppLanguageList()

    const isSystemPage = commonPageList.some(v => v.value === openPageId)

    const currentPageArgs = useMemo(() => {
        if (!openPageId || isSystemPage) {
            return
        }

        const page = allPages.find(item => item.id === openPageId)
        if (!page) {
            return
        }

        return {
            pathArgs: page.parameterAndVariable?.pathParameter,
            queryArgs: page.parameterAndVariable?.queryParameter
        }
    }, [allPages, isSystemPage, openPageId])

    const handleCreatePage = useAtomCallback(
        useCallback(
            async (get, set) => {
                const currentLang = get(languageAtom)
                const pageId = await set(createPageAtom, { name: NO_NAME_PAGE, lang: currentLang, type: 'COMMON' })
                if (pageId) {
                    set(
                        openPageStackAtom,
                        stackFactory({
                            pageId,
                            stackId: nanoid(),
                            pageStackDisplayType: pageOpenType
                        })
                    )
                }
            },
            [pageOpenType]
        )
    )

    const { type } = useFlowContainerContext()
    const isInFlow = !!type

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>页面</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <SelectPage
                            dropdownProps={{
                                style: {
                                    maxHeight: 300
                                }
                            }}
                            withinPortal
                            languageList={languageList}
                            value={openPageId}
                            options={pageTree}
                            onChange={val => onChange({ ...value, openPageId: val })}
                            canCreatePage={!isInFlow}
                            onClickCreatePage={handleCreatePage}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                {openPageId && !isSystemPage && (
                    <>
                        <ListItem4ByecodeUi>
                            <SC.FormItemLabelWrapper>
                                <SC.FormItemLabel>打开方式</SC.FormItemLabel>
                            </SC.FormItemLabelWrapper>
                            <SC.FormItemContent>
                                <SegmentedControl
                                    fullWidth
                                    data={PAGE_STACK_DISPLAY_TYPE_OPTIONS}
                                    value={pageOpenType || PAGE_STACK_DISPLAY_TYPE.modal}
                                    onChange={pageOpenType => onChange({ ...value, pageOpenType: pageOpenType as PageStackDisplayType })}
                                />
                            </SC.FormItemContent>
                        </ListItem4ByecodeUi>
                        <ListItem4ByecodeUi>
                            <SC.FormItemLabelWrapper>
                                <SC.FormItemLabel>滚动到组件</SC.FormItemLabel>
                            </SC.FormItemLabelWrapper>
                            <SC.FormItemContent>
                                <Input
                                    value={hash}
                                    placeholder="请输入锚点/组件标识"
                                    onChange={ev => onChange({ ...value, hash: ev.target.value })}
                                />
                            </SC.FormItemContent>
                        </ListItem4ByecodeUi>
                    </>
                )}
            </Group>

            {currentPageArgs?.pathArgs && currentPageArgs.pathArgs.length > 0 && (
                <>
                    <Divider />
                    <Group label="传入路径参数">
                        {(() => {
                            const parametersValue = pathParameters as
                                | { id: string; value: RichTextContentProtocol | undefined }[]
                                | undefined
                            const pathSchemas = currentPageArgs.pathArgs
                            return (
                                <>
                                    {pathSchemas?.map(item => {
                                        const v = parametersValue?.find(p => p.id === item.id)?.value
                                        return (
                                            <ListItem4ByecodeUi key={item.id}>
                                                <SC.FormItemContent style={{ flex: 1 }}>
                                                    <ArgInput
                                                        autoHeight
                                                        enableSelectDataSource
                                                        data={item}
                                                        allParentNodes={allParentNodes}
                                                        allParentActions={allParentActions}
                                                        value={v}
                                                        onChange={nextVal => {
                                                            const newParameters = pathSchemas.map(schema => {
                                                                const isCurrent = schema.id === item.id
                                                                const cur = isCurrent
                                                                    ? nextVal
                                                                    : parametersValue?.find(f => f.id === schema.id)?.value
                                                                return {
                                                                    id: schema.id,
                                                                    value: cur
                                                                }
                                                            })
                                                            onChange({ ...value, pathParameters: newParameters })
                                                        }}
                                                    />
                                                </SC.FormItemContent>
                                            </ListItem4ByecodeUi>
                                        )
                                    })}
                                </>
                            )
                        })()}
                    </Group>
                </>
            )}

            {currentPageArgs?.queryArgs && currentPageArgs.queryArgs.length > 0 && (
                <>
                    <Divider />
                    <Group label="传入查询参数">
                        {(() => {
                            const parametersValue = queryParameters as
                                | { id: string; value: RichTextContentProtocol | undefined }[]
                                | undefined
                            const querySchemas = currentPageArgs.queryArgs
                            return (
                                <>
                                    {querySchemas?.map(item => {
                                        const v = parametersValue?.find(p => p.id === item.id)?.value
                                        return (
                                            <ListItem4ByecodeUi key={item.id}>
                                                <SC.FormItemContent style={{ flex: 1 }}>
                                                    <ArgInput
                                                        autoHeight
                                                        data={item}
                                                        enableSelectDataSource
                                                        allParentNodes={allParentNodes}
                                                        allParentActions={allParentActions}
                                                        value={v}
                                                        onChange={nextVal => {
                                                            const newParameters = querySchemas.map(schema => {
                                                                const isCurrent = schema.id === item.id
                                                                const cur = isCurrent
                                                                    ? nextVal
                                                                    : parametersValue?.find(f => f.id === schema.id)?.value
                                                                return {
                                                                    id: schema.id,
                                                                    value: cur
                                                                }
                                                            })
                                                            onChange({ ...value, queryParameters: newParameters })
                                                        }}
                                                    />
                                                </SC.FormItemContent>
                                            </ListItem4ByecodeUi>
                                        )
                                    })}
                                </>
                            )
                        })()}
                    </Group>
                </>
            )}
        </SC.Container>
    )
}
